import { useEffect, useState } from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { X, LogOut } from 'lucide-react'
import supabase from '@/supabaseClient'

export const SessionExpiredBanner = () => {
  const [isSessionExpired, setIsSessionExpired] = useState(false)

  useEffect(() => {
    // Listen for the custom event dispatched by the middleware
    // @ts-ignore
    const handleSessionExpired = (event: CustomEvent) => {
      setIsSessionExpired(true)
    }

    // Add event listener
    window.addEventListener('sessionExpired', handleSessionExpired as EventListener)

    // Cleanup
    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired as EventListener)
    }
  }, [])

  const handleLogout = async () => {
    try {
      // Step 1: Preserve device-specific settings BEFORE clearing anything
      const deviceSpecificData = {
        pwa_dismissed: localStorage.getItem('pwa_install_prompt_dismissed'),
        pwa_timestamp: localStorage.getItem('pwa_dismissal_timestamp'),
        is_ios_pwa: localStorage.getItem('is_ios_pwa'),
      };
  
      // Step 2: Clear Supabase session
      await supabase.auth.signOut()
      
      // Step 3: Clear all localStorage items
      localStorage.removeItem('adminToken')
      localStorage.removeItem('vbAuth')
      localStorage.removeItem('accountType')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('expires_at')
      
      // Step 4: Clear ALL OneSignal-related data for complete isolation
      localStorage.removeItem('oneSignalId');           // ✅ Safe to clear
      localStorage.removeItem('externalUserId');        // User mapping
      localStorage.removeItem('notifications_registered'); // User preference
      localStorage.removeItem('notifications_status');     // User preference
      localStorage.removeItem('notifications_asked');
  
      // Step 5: Restore device-specific settings that should persist across logins
      if (deviceSpecificData.pwa_dismissed) {
        localStorage.setItem('pwa_install_prompt_dismissed', deviceSpecificData.pwa_dismissed);
      }
      if (deviceSpecificData.pwa_timestamp) {
        localStorage.setItem('pwa_dismissal_timestamp', deviceSpecificData.pwa_timestamp);
      }
      if (deviceSpecificData.is_ios_pwa) {
        localStorage.setItem('is_ios_pwa', deviceSpecificData.is_ios_pwa);
      }
  
      // Step 6: Redirect to login page
      window.location.href = '/login'
      
    } catch (error) {
      console.error('Error logging out:', error)
      
      // Fallback cleanup with device preservation
      const deviceData = {
        pwa_dismissed: localStorage.getItem('pwa_install_prompt_dismissed'),
        pwa_timestamp: localStorage.getItem('pwa_dismissal_timestamp'),
        is_ios_pwa: localStorage.getItem('is_ios_pwa')
      };
      
      // Clear everything in fallback
      localStorage.clear();
      
      // Restore device-specific data
      if (deviceData.pwa_dismissed) {
        localStorage.setItem('pwa_install_prompt_dismissed', deviceData.pwa_dismissed);
      }
      if (deviceData.pwa_timestamp) {
        localStorage.setItem('pwa_dismissal_timestamp', deviceData.pwa_timestamp);
      }
      if (deviceData.is_ios_pwa) {
        localStorage.setItem('is_ios_pwa', deviceData.is_ios_pwa);
      }
      
      // Force reload as fallback
      window.location.reload()
    }
  }

  const dismissBanner = () => {
    setIsSessionExpired(false)
  }

  // Don't render if session is not expired
  if (!isSessionExpired) return null

  return (
    <div className="w-full bg-destructive/10 border-b border-destructive/20 p-2">
      <Alert className="bg-transparent border-destructive/20 text-destructive">
        <LogOut className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between w-full">
          <span className="text-sm font-medium">
            Your session has expired. Please log in again or refresh the page to continue.
          </span>
          <div className="flex items-center space-x-2 ml-4">
            <Button
              size="sm"
              variant="outline"
              onClick={handleLogout}
              className="h-7 text-xs border-destructive/20 hover:bg-destructive/5"
            >
              <LogOut className="h-3 w-3 mr-1" />
              Logout
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={dismissBanner}
              className="h-7 w-7 p-0 hover:bg-destructive/5"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  )
}